// Widget registry contract (no JSX here so it can be tested in Node): every widget type declares
// its props schema (Zod) and data requirements. Adding a widget TYPE is the only frontend change
// the architecture allows; everything else is configuration.
import { z } from 'zod';

const column = z.object({
  key: z.string(),
  label: z.string(),
  format: z.string().optional(),
  metric: z.string().optional(),
  emphasis: z.boolean().optional(),
  benchmark: z.object({ min: z.number(), max: z.number() }).optional(),
  currencyKey: z.string().optional(),
}).loose();

const fieldDef = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'email', 'password', 'number', 'date', 'select', 'multiselect', 'month', 'json', 'textarea']),
  required: z.boolean().optional(),
  placeholder: z.string().optional(),
  pattern: z.string().optional(),
  options: z.array(z.union([z.string(), z.object({ value: z.string(), label: z.string() })])).optional(),
  optionsFrom: z.string().optional(),
  default: z.unknown().optional(),
  showIf: z.record(z.string(), z.unknown()).optional(),
  help: z.string().optional(),
});

const action = z.object({
  id: z.string(),
  label: z.string(),
  scope: z.enum(['row', 'table']).optional(),
  confirm: z.string().optional(),
  when: z.record(z.string(), z.unknown()).optional(),
  input: z.union([z.object({ type: z.literal('json'), field: z.string() }), z.object({ fields: z.array(fieldDef) })]).optional(),
  opens: z.string().optional(),
}).loose();

const benchmark = z.object({ min: z.number(), max: z.number() });

export const WIDGET_SCHEMAS = {
  'metric-card': {
    props: z.object({ variant: z.enum(['default', 'hero']).optional(), precise: z.boolean().optional(), format: z.string().optional(), sparkline: z.boolean().optional(), showRateCitation: z.boolean().optional(), benchmark: benchmark.optional() }).loose(),
    dataRequirements: ['value', 'change', 'trend'],
  },
  'line-chart': {
    props: z.object({ area: z.boolean().optional(), benchmark: benchmark.optional() }).loose(),
    dataRequirements: ['timeseries[]'],
  },
  'bar-chart': {
    props: z.object({
      orientation: z.enum(['vertical', 'horizontal']).optional(),
      grouped: z.boolean().optional(),
      valueKey: z.string().optional(),
      labelKey: z.string().optional(),
      markerKey: z.string().optional(),
      markerLabel: z.string().optional(),
      valueLabel: z.string().optional(),
      format: z.string().optional(),
    }).loose(),
    dataRequirements: ['categories[]', 'values[]'],
  },
  'data-table': {
    props: z
      .object({
        columns: z.array(column).optional(),
        totals: z.boolean().optional(),
        pageSize: z.number().int().positive().optional(),
        // "cards": each row as a card (title, subtitle, one main value, the rest listed).
        view: z.enum(['table', 'cards']).optional(),
        card: z.object({ title: z.string().optional(), subtitle: z.string().optional(), value: z.string().optional() }).optional(),
      })
      .loose(),
    dataRequirements: ['rows[]', 'columns[]'],
  },
  'live-feed': { props: z.object({}).loose(), dataRequirements: ['items[] (streaming)'] },
  funnel: { props: z.object({}).loose(), dataRequirements: ['stages[]'] },
  heatmap: { props: z.object({}).loose(), dataRequirements: ['matrix[][]'] },
  'sync-panel': {
    props: z.object({ actions: z.array(action).optional() }).loose(),
    dataRequirements: ['connectors[]'],
  },
  'data-status': {
    // Which connectors to show, in this order (default: every connector of the selected stores).
    props: z.object({ connectors: z.array(z.string().min(1)).optional() }).loose(),
    dataRequirements: ['connectors[]'],
  },
  'alert-list': { props: z.object({ allowAcknowledge: z.boolean().optional() }).loose(), dataRequirements: ['alerts[]'] },
  'crud-table': {
    props: z.object({ columns: z.array(column), actions: z.array(action).optional(), pageSize: z.number().int().positive().optional(), emptyText: z.string().optional() }).loose(),
    dataRequirements: ['rows[]', 'actions[]'],
  },
  wizard: {
    props: z.object({
      submitAction: z.string(),
      steps: z.array(z.object({ id: z.string(), title: z.string(), fields: z.array(fieldDef).optional(), review: z.boolean().optional(), note: z.string().optional() })).min(1),
    }).loose(),
    dataRequirements: ['steps[]'],
  },
  builder: { props: z.object({}).loose(), dataRequirements: ['palette', 'canvas'] },
  'currency-table': { props: z.object({ showMonthlySplit: z.boolean().optional() }).loose(), dataRequirements: ['rows[]', 'rate'] },
  'ai-assistant': { props: z.object({ suggestions: z.array(z.string()).optional() }).loose(), dataRequirements: ['chat interface'] },
} as const;

export type WidgetType = keyof typeof WIDGET_SCHEMAS;

export function validateWidgetProps(type: string, props: unknown): { ok: true; props: any } | { ok: false; error: string } {
  const entry = (WIDGET_SCHEMAS as Record<string, { props: z.ZodType }>)[type];
  if (!entry) return { ok: false, error: `Unknown widget type "${type}"` };
  const res = entry.props.safeParse(props ?? {});
  if (!res.success) return { ok: false, error: res.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
  return { ok: true, props: res.data };
}
